import axios from 'axios'

const defaultOverlayConfig = {
  mustBeAuthenticated: true,
  retry: true,
  allowRedirect: true,
  functions: {
    getToken: () => {
      return window.localStorage.getItem('token')
    },
    setToken: (token) => {
      window.localStorage.setItem('token', token)
    },
    logout: () => {
      window.localStorage.setItem('token', null)
    },
    setHeaders: (instance) => {
      instance.defaults.headers.common['Accept'] = 'application/json'
      return instance
    },
    errorsConfig: {},
    errorsHandlers: {
      default: ({ error, reject }) => {
        reject(error)
      }
    }
  }
}

export default function axiosOverlay(axiosConfig, overlayConfig = defaultOverlayConfig) {
  // if config missing in overlayConfig pick the default one
  overlayConfig = { ...defaultOverlayConfig, ...overlayConfig }
  overlayConfig.functions = { ...defaultOverlayConfig.functions, ...overlayConfig.functions }
  overlayConfig.functions.errorsConfig = {
    ...defaultOverlayConfig.functions.errorsConfig,
    ...overlayConfig.functions.errorsConfig
  }
  overlayConfig.functions.errorsHandlers = {
    ...defaultOverlayConfig.functions.errorsHandlers,
    ...overlayConfig.functions.errorsHandlers
  }

  // Capture le token au moment de la requête
  const initialToken = overlayConfig.functions.getToken()

  const instance = axios.create()

  // setHeaders d'abord pour permettre la personnalisation
  overlayConfig.functions.setHeaders(instance)

  // Puis les headers d'authentification
  if (overlayConfig.mustBeAuthenticated && initialToken) {
    instance.defaults.headers.common['Authorization'] = 'Bearer ' + initialToken
  }

  // Enfin les bonus headers qui peuvent tout surcharger
  if (overlayConfig.bonusHeader) {
    for (const [key, value] of Object.entries(overlayConfig.bonusHeader)) {
      instance.defaults.headers.common[key] = value
    }
  }

  return new Promise((resolve, reject) => {
    instance(axiosConfig)
      .then((response) => {
        resolve(response)
      })
      .catch(async (error) => {
        // Gestion du refresh token automatique (code 449)
        if (error.response && error.response.status === 449 && overlayConfig.retry) {
          try {
            overlayConfig.functions.setToken(error.response.data.token)

            // Vérifie que le token a bien été mis à jour
            const newToken = overlayConfig.functions.getToken()
            if (newToken && newToken !== initialToken) {
              // Clone la config pour éviter les mutations
              const retryConfig = {
                ...overlayConfig,
                retry: false // Évite la boucle infinie
              }
              return resolve(axiosOverlay(axiosConfig, retryConfig))
            }
          } catch (tokenError) {
            console.error('Erreur lors du setToken:', tokenError)
          }
        }

        // Fonction retry pour permettre la relance de la requête
        // Récupère le token mis à jour et évite de repasser par les handlers personnalisés
        const retry = () => {
          const retryConfig = {
            ...overlayConfig,
            functions: {
              ...overlayConfig.functions,
              // Garde seulement le handler par défaut pour éviter la boucle
              errorsHandlers: {
                default: overlayConfig.functions.errorsHandlers.default
              }
            }
          }
          return axiosOverlay(axiosConfig, retryConfig)
        }

        // Vérifie si un handler personnalisé existe pour ce statut
        const hasCustomHandler =
          error.response?.status && overlayConfig.functions.errorsHandlers[error.response.status]

        // Gestion des erreurs d'authentification 401
        // N'exécute le comportement par défaut que s'il n'y a pas de handler personnalisé
        if (
          error.response &&
          error.response.status === 401 &&
          overlayConfig.mustBeAuthenticated &&
          !hasCustomHandler
        ) {
          if (!overlayConfig.functions.errorsConfig[401]?.dontLogout) {
            try {
              overlayConfig.functions.logout()
            } catch (logoutError) {
              console.error('Erreur lors du logout:', logoutError)
            }
          }

          if (!overlayConfig.functions.errorsConfig[401]?.dontStopAll) {
            window.stop()
          }
        }

        // Gestion des sessions expirées 498
        if (
          error.response &&
          error.response.status === 498 &&
          overlayConfig.mustBeAuthenticated &&
          !hasCustomHandler
        ) {
          if (!overlayConfig.functions.errorsConfig[498]?.dontLogout) {
            try {
              overlayConfig.functions.logout()
            } catch (logoutError) {
              console.error('Erreur lors du logout:', logoutError)
            }
          }

          if (!overlayConfig.functions.errorsConfig[498]?.dontStopAll) {
            window.stop()
          }
        }

        // Appel du handler d'erreur approprié
        if (hasCustomHandler) {
          overlayConfig.functions.errorsHandlers[error.response.status]({
            error,
            axiosConfig,
            overlayConfig,
            resolve,
            reject,
            retry
          })
        } else {
          overlayConfig.functions.errorsHandlers.default({
            error,
            axiosConfig,
            overlayConfig,
            resolve,
            reject,
            retry
          })
        }
      })
  })
}
